
// Vuex/Vue
import { Component, Vue, Prop, Watch, Emit } from "vue-property-decorator";
import { Mutation, namespace } from "vuex-class";

// Types
import { LDAPAddress, LDAPPort } from "@/store/ldap-management/types";

// Third Party
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import { VueBootstrapField } from "@/store/types";
import { isEmpty, cloneDeep } from "lodash";
import AddNewLDAPAddress from "./AddNewLDAPAddress.vue";
import ConfirmationModal from "@/components-shared/ConfirmationModal.vue";
import { maxLength, minLength, required, url } from "vuelidate/lib/validators";
import { validationMixin } from "vuelidate";

// Namespaces
const LDAPManagement = namespace("ldapManagement");

@Component({
	mixins: [validationMixin],
	validations: {
		ldapAddresses: {
			$each: {
				address: {
					required,
					url,
					minLength: minLength(1),
					maxLength: maxLength(250)
				}
			}
		}
	},
	components: {
		"vue-perfect-scrollbar": VuePerfectScrollbar,
		"add-new-ldap-address": AddNewLDAPAddress,
		"confirmation-modal": ConfirmationModal
	}
})
export default class LDAPManagementModal extends Vue {
	// Const
	private readonly portLength: number = 5;

	// Vuex
	@Mutation private setIsLoading: (loading: boolean) => void;

	@LDAPManagement.Action loadLDAPPort: () => Promise<void>;
	@LDAPManagement.Action updateLDAPPort: (newPort: LDAPPort) => Promise<void>;

	@LDAPManagement.Action loadLDAPAddresses: () => Promise<void>;
	@LDAPManagement.Action removeLDAPAddress: (ldapAddress: LDAPAddress) => Promise<void>;
	@LDAPManagement.Action addNewLDAPAddress: (ldapAddress: LDAPAddress) => Promise<void>;
	@LDAPManagement.Action updateLDAPAddress: ({ previousAddress, newAddress }) => Promise<void>;

	@LDAPManagement.Getter("getLDAPAddesses") private ldapAddresses: LDAPAddress[];
	@LDAPManagement.Getter("getLDAPPort") private ldapPort: LDAPPort;

	// -- Props -- //
	@Prop({ default: false, type: Boolean }) modalVisible: boolean;
	@Prop({ default: false, type: Boolean }) ldapCheckboxState: boolean;

	// -- Properties -- //
	private modalStateSet: boolean = false;
	private ldapAddressesLoaded: boolean = false;
	private showConfirmation: boolean = false;
	private itemToDelete: LDAPAddress = null;
	private addingNewAddress: boolean = false;
	private isLDAPAddressEditing: boolean = false;
	private isLDAPPortEditing: boolean = false;
	private isLDAPPortVisible: boolean = true;
	private editedLDAPAddress: LDAPAddress = null;
	private originalLDAPAddress: LDAPAddress = null;
	private editedLDAPPort: LDAPPort = null;
	private showErrorAnimation: boolean = false;
	private hasValidationErrors: boolean = false;
	private hasAddressValidationErrors: boolean = false;
	private searchTerm: string = "";
	private newLDAPAddress: LDAPAddress = {
		address: ""
	};
	private newLDAPPort: LDAPPort = {
		port: null
	};

	// Table's fields
	private fields: VueBootstrapField[] = [
		{ key: "address", label: "Address", sortable: false },
		{ key: "actions", label: "Actions", sortable: false, thClass: "text-right" }
	];

	// -- Watchers -- //
	@Watch("modalVisible")
	private async setState(newModalState: boolean): Promise<void> {
		if (!newModalState) {
			this.modalStateSet = false;
			this.ldapAddressesLoaded = false;
			return;
		}

		await this.setData();
		this.ldapAddressesLoaded = true;
		this.modalStateSet = true;
	}

	@Watch("ldapManagementModalLoaded")
	private onLDAPManagementModalLoaded(newValue: boolean, oldValue: boolean) {
		if (newValue) {
			this.setIsLoading(false);
		}
	}

	// -- Computed Properties -- //
	private get ldapManagementModalLoaded(): boolean {
		return this.modalStateSet && this.ldapAddressesLoaded;
	}

	public get showEditControls() {
		return (ldapAddress: LDAPAddress) => this.isLDAPAddressEditing && ldapAddress === this.editedLDAPAddress;
	}

	public get showEditPortControls() {
		return () => this.isLDAPPortEditing;
	}

	// -- Methods -- //
	private async setData(): Promise<void> {
		await this.loadLDAPPort();
		this.editedLDAPPort = cloneDeep(this.newLDAPPort);
		this.ldapCheckboxState = !(this.ldapPort.port == null);
		this.isLDAPPortVisible = this.ldapCheckboxState;

		if (!this.ldapAddresses || isEmpty(this.ldapAddresses)) {
			await this.loadLDAPAddresses();
		}
	}

	private async deleteLDAPAddress(): Promise<void> {
		this.showConfirmation = false;
		await this.removeLDAPAddress(this.itemToDelete);
		this.itemToDelete = null;
	}

	private onToggleConfirmation(ldapAddress: LDAPAddress): void {
		this.itemToDelete = ldapAddress;
		this.showConfirmation = true;
	}

	private onToggleUpdate(ldapAddressToEdit: LDAPAddress): void {
		this.cancelNotSavedValue();

		this.isLDAPAddressEditing = true;
		this.editedLDAPAddress = ldapAddressToEdit;
		this.originalLDAPAddress = cloneDeep(ldapAddressToEdit);
	}

	private cancelNotSavedValue(): void {
		if (this.isLDAPAddressEditing) {
			let notSavedItem = this.ldapAddresses.find(x => x.address === this.editedLDAPAddress.address);
			if (notSavedItem !== undefined) {
				notSavedItem.address = this.originalLDAPAddress.address;
			}
			this.editedLDAPAddress = null;
		}
	}

	private onTogglePortUpdate(): void {
		this.isLDAPPortEditing = true;
		this.editedLDAPPort = cloneDeep(this.ldapPort);
	}

	private async onToggleSaveUpdate(newAddress: LDAPAddress): Promise<void> {
		this.triggerValidation();
		if (!this.$v.$invalid && !this.hasAddressValidationErrors) {
			this.editedLDAPAddress = newAddress;
			this.isLDAPAddressEditing = false;
			await this.updateLDAPAddress({ previousAddress: this.originalLDAPAddress, newAddress });
		}
	}

	private isPortValid(): boolean {
		if (this.editedLDAPPort === null || this.editedLDAPPort.port === null) {
			return true;
		}
		const portLength = this.editedLDAPPort.port.toString().length;
		return portLength > 0 && portLength <= this.portLength;
	}

	private getPortError(): string {
		if (this.editedLDAPPort.port === null) {
			return "Port is required";
		}
		else {
			if (this.editedLDAPPort.port.toString().length > this.portLength) {
				return "Port max length is " + this.portLength;
			}
			else {
				return "Port is required";
			}
		}
	}

	private async onToggleSaveUpdatePort(): Promise<void> {
		if (this.isPortValid()) {
			this.hasValidationErrors = false;
			this.isLDAPPortEditing = false;
			await this.updateLDAPPort(this.editedLDAPPort);
		} else {
			this.hasValidationErrors = true;
			this.showErrorAnimation = true;
		}

		setTimeout(() => {
			this.showErrorAnimation = false;
		}, this.$config.ANIMATION_DURATION);
	}

	private onToggleCancelUpdate(item: LDAPAddress): void {
		this.isLDAPAddressEditing = false;
		item.address = cloneDeep(this.originalLDAPAddress.address);
	}

	private onToggleCancelUpdatePort(): void {
		this.isLDAPPortEditing = false;
		this.editedLDAPPort = cloneDeep(this.ldapPort);
	}

	private async onAddNewAddress(newLDAPAddress: LDAPAddress): Promise<void> {
		let ldapAddress: LDAPAddress = { ...newLDAPAddress };
		this.addingNewAddress = false;
		await this.addNewLDAPAddress(ldapAddress);
	}

	private async onToggleLDAPPortCheckbox(checked): Promise<void> {
		this.isLDAPPortVisible = checked;
		if (!checked) {
			await this.updateLDAPPort(this.newLDAPPort);
		}
	}

	private triggerValidation(): void {
		this.hasAddressValidationErrors = this.hasDuplication();
	}

	private hasDuplication(): boolean {
		let addresses = this.ldapAddresses.map(item => item.address);
		return new Set(addresses).size !== addresses.length;
	}

	private hasAddressError(item: LDAPAddress): boolean {
		var index = this.ldapAddresses.indexOf(item);
		return this.$v.ldapAddresses.$each[index].address.$invalid
			|| (this.editedLDAPAddress !== null
				&& this.editedLDAPAddress.address === item.address
				&& this.hasDuplication());
	}

	private getAddressError(item: LDAPAddress): string {
		var errorText = "";
		var index = this.ldapAddresses.indexOf(item);
		var element = this.$v.ldapAddresses.$each[index].address;
		if (this.hasDuplication()) {
			errorText = "Similar item already exists";
		} else if (element.url) {
			errorText = "Invalid format of url";
		} else if (element.required) {
			errorText = "Invalid format of url";
		} else if (element.minLength) {
			errorText = "Min length is 1";
		} else if (element.maxLength) {
			errorText = "Max length is 250";
		}
		return errorText;
	}

	@Emit("toggle-modal")
	private closeModal(): void {
		this.addingNewAddress = false;
		this.showConfirmation = false;
		this.searchTerm = "";
		this.cancelNotSavedValue();
		this.isLDAPPortVisible = false;
		this.isLDAPAddressEditing = false;
		this.editedLDAPAddress = null;
		this.hasAddressValidationErrors = false;
		this.onToggleCancelUpdatePort();
	}
}
